import React, { useMemo, useRef, useCallback, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { ElectricalSafetySection } from './ElectricalSafetyTable';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface ElectricalSafetyChartProps {
  sections: ElectricalSafetySection[];
  /** Used for the download filename and chart subtitle */
  reportName?: string;
  reportDate?: string;
  premisesName?: string;
  onCanvasReady?: (getImage: () => string) => void;
}

function splitLabel(name: string): string[] {
  const abbrevMap: Record<string, string> = {
    ELECTRICAL: 'Elec.', PROTECTION: 'Prot.', PREVENTIVE: 'Prev.',
    PREDICTIVE: 'Pred.', MAINTENANCE: 'Maint.', STATUTORY: 'Stat.',
    COMPLIANCE: 'Compl.', AWARENESS: 'Aware.', TRAINING: 'Train.',
    EMERGENCY: 'Emerg.', LIGHTNING: 'Light.', PERSONAL: 'Personal',
    EQUIPMENT: 'Equip.', LOCKOUT: 'Lockout', SYSTEMS: 'Sys.',
    SYSTEM: 'Sys.', BACKUP: 'Backup', SAFETY: 'Safety',
    TAGOUT: 'Tagout', ORIGIN: 'Origin', SHOCK: 'Shock',
    FIRE: 'Fire', WORK: 'Work', PERMIT: 'Permit', PROTECTIVE: 'Prot.',
  };
  const words = name.toUpperCase().split(/[\s\/\-&,]+/).filter(Boolean)
    .map(w => abbrevMap[w] ?? (w.length > 7 ? w.slice(0, 6) + '.' : w));
  if (words.length <= 2) return words;
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
}

function makeBarLabelPlugin(scores: number[], percentages: number[], maxScores: number[]) {
  return {
    id: 'elecBarLabels',
    afterDatasetsDraw(chart: any) {
      const { ctx } = chart;
      chart.data.datasets.forEach((dataset: any, di: number) => {
        const meta = chart.getDatasetMeta(di);
        if (!meta?.data?.length) return;
        meta.data.forEach((bar: any, idx: number) => {
          const val: number = dataset.data[idx];
          if (!val) return;
          const x = bar.x, top = bar.y;
          const bottom = bar.base ?? top + 1;
          const h = Math.abs(bottom - top);
          ctx.save();
          ctx.textAlign = 'center';
          if (di === 0) {
            const label = String(maxScores[idx] ?? val);
            ctx.font = 'bold 11px Arial';
            if (h > 22) {
              ctx.fillStyle = 'rgba(255,255,255,0.97)'; ctx.textBaseline = 'middle';
              ctx.fillText(label, x, top + h / 2);
            } else {
              ctx.fillStyle = '#4b5563'; ctx.textBaseline = 'bottom';
              ctx.fillText(label, x, top - 4);
            }
          } else {
            const score = scores[idx], pct = percentages[idx];
            const line1 = score != null ? score.toFixed(1) : String(val);
            const line2 = pct   != null ? `(${pct.toFixed(0)}%)` : '';
            ctx.font = 'bold 11px Arial';
            if (h > 46) {
              ctx.fillStyle = 'rgba(255,255,255,0.97)'; ctx.textBaseline = 'middle';
              ctx.fillText(line1, x, top + h / 2 - 9);
              if (line2) { ctx.font = '10px Arial'; ctx.fillText(line2, x, top + h / 2 + 9); }
            } else if (h > 24) {
              ctx.fillStyle = 'rgba(255,255,255,0.97)'; ctx.textBaseline = 'middle';
              ctx.fillText(line2 ? `${line1} ${line2}` : line1, x, top + h / 2);
            } else {
              ctx.fillStyle = '#4b5563'; ctx.textBaseline = 'bottom';
              ctx.fillText(line2 ? `${line1} ${line2}` : line1, x, top - 4);
            }
          }
          ctx.restore();
        });
      });
    },
  };
}

const ElectricalSafetyChart: React.FC<ElectricalSafetyChartProps> = ({
  sections,
  reportName,
  reportDate,
  premisesName,
  onCanvasReady,
}) => {
  const chartRef = useRef<any>(null);

    // ── Notify parent with getImage() after chart renders ────────────────────
  useEffect(() => {
    if (!onCanvasReady) return;
    const t = setTimeout(() => {
      onCanvasReady(() => {
        const chart = chartRef.current;
        if (!chart) return '';
        const src = chart.canvas as HTMLCanvasElement;
        const off = document.createElement('canvas');
        off.width = src.width; off.height = src.height;
        const ctx = off.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, off.width, off.height);
        ctx.drawImage(src, 0, 0);
        return off.toDataURL('image/png');
      });
    }, 300);
    return () => clearTimeout(t);
  }, [onCanvasReady, sections]);

  // ── All hooks before early return ──────────────────────────────────────────
  const yMax = useMemo(() => {
    if (!sections.length) return 30;
    return Math.ceil(Math.max(...sections.map(s => s.totalMaxScore)) * 1.28);
  }, [sections]);

  const plugin = useMemo(
    () => makeBarLabelPlugin(
      sections.map(s => s.totalScore),
      sections.map(s => s.percentage),
      sections.map(s => s.totalMaxScore),
    ),
    [sections],
  );

  const splitLabels = useMemo(() => sections.map(s => splitLabel(s.name)), [sections]);

  const chartData = useMemo(() => ({
    labels: splitLabels,
    datasets: [
      {
        label: 'Max Score',
        data: sections.map(s => s.totalMaxScore),
        backgroundColor: 'rgba(156,163,175,0.55)',
        borderColor: 'rgba(107,114,128,0.85)',
        borderWidth: 1.5, borderRadius: 3,
        categoryPercentage: 0.82, barPercentage: 1.0,
      },
      {
        label: 'Score',
        data: sections.map(s => s.totalScore),
        backgroundColor: 'rgba(132,51,173,1)',
        borderColor: 'rgba(102,0,153,1)',
        borderWidth: 1.5, borderRadius: 3,
        categoryPercentage: 0.82, barPercentage: 1.0,
      },
    ],
  }), [sections, splitLabels]);

  const subtitleParts = [
    premisesName,
    reportDate ? new Date(reportDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null,
  ].filter(Boolean) as string[];

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 36, left: 4, right: 4, bottom: 4 } },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: { font: { size: 12 }, padding: 16, usePointStyle: true, pointStyle: 'rect' as const },
      },
      title: {
        display: true,
        text: subtitleParts.length
          ? ['Electrical Safety Scorecard — Section Scores', subtitleParts.join(' · ')]
          : 'Electrical Safety Scorecard — Section Scores',
        font: { size: 14, weight: 'bold' as const },
        padding: { top: 4, bottom: 12 },
        color: '#1f2937',
      },
      tooltip: {
        backgroundColor: 'rgba(17,24,39,0.92)', padding: 12, cornerRadius: 8,
        callbacks: {
          title: (items: any[]) => sections[items[0]?.dataIndex]?.name ?? '',
          label: (ctx: any) => {
            const s = sections[ctx.dataIndex];
            if (!s) return '';
            if (ctx.datasetIndex === 0) return `Max Score: ${s.totalMaxScore.toFixed(2)}`;
            return `Score: ${s.totalScore.toFixed(2)} / ${s.totalMaxScore.toFixed(2)} (${s.percentage.toFixed(1)}%)`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, color: '#6b7280', maxRotation: 0, minRotation: 0, autoSkip: false },
      },
      y: {
        beginAtZero: true, max: yMax,
        grid: { color: 'rgba(0,0,0,0.06)' },
        ticks: { font: { size: 11 }, color: '#6b7280' },
        title: { display: true, text: 'Score', font: { size: 12, weight: 'bold' as const }, color: '#374151' },
      },
    },
  }), [sections, yMax, subtitleParts]);

  const buildFileName = useCallback(() => {
    const parts = ['Electrical_Safety_Scorecard'];
    if (premisesName) parts.push(premisesName.replace(/[^a-zA-Z0-9]/g, '_'));
    if (reportDate)   parts.push(reportDate.replace(/[^0-9\-]/g, ''));
    else              parts.push(new Date().toISOString().slice(0, 10));
    return parts.join('_') + '.png';
  }, [premisesName, reportDate]);

  const handleDownload = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) return;

    // Draw white background before exporting (canvas is transparent by default)
    const srcCanvas: HTMLCanvasElement = chart.canvas;
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width  = srcCanvas.width;
    exportCanvas.height = srcCanvas.height;
    const ctx = exportCanvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    ctx.drawImage(srcCanvas, 0, 0);

    const a   = document.createElement('a');
    a.href     = exportCanvas.toDataURL('image/png');
    a.download = buildFileName();
    a.click();
  }, [buildFileName]);

  // ── Early return after all hooks ───────────────────────────────────────────
  if (!sections.length) return null;

  return (
    <div className="w-full mt-4 bg-white p-4 rounded-lg border border-gray-200">
      {/* Header: title + download */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-gray-800">Electrical Safety Scorecard</h3>
          {subtitleParts.length > 0 && (
            <p className="text-xs text-gray-500 mt-0.5">{subtitleParts.join(' · ')}</p>
          )}
        </div>
        <button
          onClick={handleDownload}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#00A8A5] border border-[#00A8A5] rounded-lg hover:bg-[#00A8A5] hover:text-white transition-colors"
          title={`Download as ${buildFileName()}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download PNG
        </button>
      </div>

      {/* Summary strip */}
      {/* <div className="mb-4 flex flex-wrap items-center justify-center gap-0 divide-x divide-teal-100 bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg border border-teal-100">
        {sections.map(s => (
          <div key={s.id} className="text-center px-5 py-3 flex-1 min-w-[100px]">
            <div className="text-xs font-semibold text-gray-500 truncate">{s.name}</div>
            <div className="text-lg font-bold text-teal-700 mt-0.5">
              {s.totalScore.toFixed(1)}
              <span className="text-gray-400 font-normal text-sm"> / {s.totalMaxScore}</span>
            </div>
            <div className={`text-sm font-bold mt-0.5 ${s.percentage >= 80 ? 'text-green-600' : s.percentage >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
              {s.percentage.toFixed(1)}%
            </div>
          </div>
        ))} */}
      {/* </div> */}

      <div className="w-full h-[500px]">
        <Bar ref={chartRef} data={chartData} options={chartOptions as any} plugins={[plugin]} />
      </div>
    </div>
  );
};

export default ElectricalSafetyChart;
